import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { Form, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { useTranslation, withTranslation } from "react-i18next";
import { BiSolidDownvote, BiSolidUpvote } from "react-icons/bi";
import { BsCircle, BsFillCircleFill, BsFillSquareFill, BsInfoCircle } from "react-icons/bs";
import { FcFinePrint } from "react-icons/fc";
import { useNavigate } from "react-router-dom";
import { LoaderContext } from "../../../App";
import { LS_COMPANY_INFO_KEY, LS_REGULATION_SELECTED_KEY, LS_DCE_SUBMISSION_FILTER_KEY } from "../../../core/constants/common";
import useHasPermission from "../../../core/hooks/useHasPermission";
import FormService from "../../../core/service/FormService";
import HomePageService from "../../../core/service/HomePageService";
import ReportService from "../../../core/service/ReportService";
import Utils from "../../../core/utilities/Utils";
import BranchSubmissionsSummaryModal from "./BranchSubmissionsSummaryModal";
import BranchStatusListModal from "./BranchStatusListModal";
import "./HomePage.scss";

const MONTH_NAME_TO_NUM = { JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6, JULY: 7, AUGUST: 8, SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12 };

function getFyPeriodFromCompanyInfo(selectedYear, financialYearStart, financialYearEnd) {
    const startMonth = MONTH_NAME_TO_NUM[financialYearStart] ?? 4;
    const endMonth = MONTH_NAME_TO_NUM[financialYearEnd] ?? 3;
    const y = Number(selectedYear) || new Date().getFullYear();
    const pad = (n) => String(n).padStart(2, '0');
    let startYear = y;
    let endYear = y;
    if (startMonth > endMonth) {
        startYear = y - 1;
        endYear = y;
    }
    const lastDay = new Date(endYear, endMonth, 0).getDate();
    return {
        periodStart: `${startYear}-${pad(startMonth)}-01`,
        periodEnd: `${endYear}-${pad(endMonth)}-${pad(lastDay)}`
    };
}

const DataBlock = ({ t, selectedYear }) => {
    const { setLoaderDisplay } = useContext(LoaderContext);
    const [dataCompletionProgress, setDataCompletionProgress] = useState({});
    const [dataColCount, setDataColCount] = useState({});
    const [dtPercentage, setDtPercentage] = useState(0);
    const [grossPercentage, setGrossPercentage] = useState(0);
    const [activeAssessments, setActiveAssessments] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage] = useState(2);
    const [itemsTotalCount, setItemsTotalCount] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const scrollContainerRef = useRef(null);
    const previousItemsCountRef = useRef(0);

    const navigate = useNavigate();
    const [performanceHighlights, setPerformanceHighlights] = useState([]);
    const { k, i18n } = useTranslation();
    const { hasDataCollectionViewPermission, hasDashboardViewPermission } = useHasPermission();


    const [filter, setFilter] = useState({
        category: 'ENVIRONMENTAL'
    });
    const [branchSubmissionsSummary, setBranchSubmissionsSummary] = useState(null);
    const [branchSubmissionsFrequency, setBranchSubmissionsFrequency] = useState(null);
    const [showBranchSummaryModal, setShowBranchSummaryModal] = useState(false);
    const [statusDrilldown, setStatusDrilldown] = useState(null);
    const [statusDrilldownRows, setStatusDrilldownRows] = useState([]);
    const glbRegulationId = localStorage.getItem(LS_REGULATION_SELECTED_KEY);
    // const yearOptions = [];
    // for (let i = currentYear; i >= startYear; i--) {
    //     yearOptions.push(<option value={i} key={i}>{i}</option>);
    // }

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(0);
        setActiveAssessments([]);
        previousItemsCountRef.current = 0; // Reset previous count when filters change
    }, [filter, glbRegulationId, selectedYear]);

    useEffect(() => {
        if (!glbRegulationId || !selectedYear) return;

        getDataColCount();
        getDataCompeltionProgress();
        // getPerformanceHighlights();
        getActiveAssessments(0, false); // Initial load: page 0, not appending
    }, [filter, glbRegulationId, selectedYear]);

    // Scroll to bottom when new items are loaded
    useEffect(() => {
        // Only scroll if items were added (not on initial load or filter change)
        if (activeAssessments.length > previousItemsCountRef.current && previousItemsCountRef.current > 0) {
            // Use setTimeout to ensure DOM is updated before scrolling
            setTimeout(() => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
                }
            }, 100);
        }
        // Update the previous count
        previousItemsCountRef.current = activeAssessments.length;
    }, [activeAssessments.length]);

    const getDataCompeltionProgress = () => {
        setLoaderDisplay(true);
        const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
        const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
        const isFinancialYear = dataFreqType === 'FY';
        const period = selectedYear ? `${selectedYear}-01-01` : '';
        const { periodStart, periodEnd } = isFinancialYear
            ? getFyPeriodFromCompanyInfo(selectedYear, companyInfo?.financialYearStart, companyInfo?.financialYearEnd)
            : { periodStart: '', periodEnd: '' };
        ReportService.getDataCompletionProgress(glbRegulationId, period, false, periodStart, periodEnd).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;
            setDataCompletionProgress(res.data.data);
            renderValues(res.data.data);
        }).catch(error => {
            setLoaderDisplay(false);
        });
    };

    const renderValues = (info) => {
        setDtPercentage(info?.find(p => p.inboundType === 'DATA_POINT')?.percentage ?? 0);
        const mtPct = info?.find(p => p.inboundType === 'MT_QUESTION')?.percentage ?? 0;
        const gdPct = info?.find(p => p.inboundType === 'GD_QUESTION')?.percentage ?? 0;
        setGrossPercentage((mtPct + gdPct) / 2);
    }

    const getDataColCount = () => {
        setLoaderDisplay(true);
        const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
        const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
        const isFinancialYear = dataFreqType === 'FY';
        const period = `${selectedYear}-01-01`;
        const fyPeriod = isFinancialYear ? getFyPeriodFromCompanyInfo(selectedYear, companyInfo?.financialYearStart, companyInfo?.financialYearEnd) : null;
        const periodStart = fyPeriod?.periodStart ?? null;
        const periodEnd = fyPeriod?.periodEnd ?? null;
        HomePageService.getDataCollectionCount(period, periodStart, periodEnd).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;
            setDataColCount(res.data);
        }).catch(error => {
            setLoaderDisplay(false);
        });
    };

    const getColor = (percentage) => {
        if (percentage < 80) {
            return '#ef4838';  // Red
        } else if (percentage < 90) {
            return '#FFBF00';  // Yellow
        } else {
            return '#5abb6a';  // Green
        }
    };

    const gotoDataCollection = (e) => {
        e?.preventDefault();
        navigate("../assessments");
    }

    const gotoDashboard = (e) => {
        e?.preventDefault();
        navigate("../dashboard");
    }

    const getPerformanceHighlights = () => {
        setLoaderDisplay(true);
        HomePageService.getPerfHighlights(`${selectedYear}-01-01`, filter.category, glbRegulationId).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;
            setPerformanceHighlights(res.data);

        }).catch(error => {
            setLoaderDisplay(false);
        });
    };

    const getActiveAssessments = (pageNum = null, append = false) => {
        const page = pageNum !== null ? pageNum : currentPage;
        const shouldShowLoader = !append; // Only show main loader on initial load, not on "load more"
        
        if (shouldShowLoader) {
            setLoaderDisplay(true);
        } else {
            setIsLoadingMore(true);
        }

        const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
        const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
        const isFinancialYear = dataFreqType === 'FY';
        const fyPeriod = isFinancialYear ? getFyPeriodFromCompanyInfo(selectedYear, companyInfo?.financialYearStart, companyInfo?.financialYearEnd) : null;
        const periodStart = fyPeriod?.periodStart ?? null;
        const periodEnd = fyPeriod?.periodEnd ?? null;
        const yearParam = isFinancialYear ? null : selectedYear;
        FormService.listActiveAssessments(page, itemsPerPage, 'reportingPeriod,desc', 'GENERAL', true, 'ACTIVE', true, yearParam, periodStart, periodEnd).then(res => {
            if (shouldShowLoader) {
                setLoaderDisplay(false);
            } else {
                setIsLoadingMore(false);
            }
            
            if (res.status !== 200) return;
            
            // API returns array directly or wrapped in content property
            const assessments = Array.isArray(res.data) ? res.data : (res.data?.content || []);
            
            // Extract total count from response headers (only set on first load)
            if (!append && res.headers?.["x-total-count"]) {
                const totalCount = parseFloat(res.headers["x-total-count"]);
                setItemsTotalCount(totalCount);
            }
            
            // Append new items to existing ones, or replace if it's the initial load
            if (append) {
                setActiveAssessments(prev => [...prev, ...assessments]);
            } else {
                setActiveAssessments(assessments);
            }
        }).catch(error => {
            if (shouldShowLoader) {
                setLoaderDisplay(false);
            } else {
                setIsLoadingMore(false);
            }
            console.error('Error fetching active assessments:', error);
        });
    };

    const handleViewMore = () => {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        getActiveAssessments(nextPage, true); // Append mode
    };

    const renderEmptyDataLabel = () => {
        return (
            <tr>
                <td>
                    <div className="Nodatafound_box">
                        <h6><FcFinePrint className="me-2" />{t("Nodatafound.label")}</h6>
                    </div>
                </td>
            </tr>
        )
    };

    const renderESGSwitch = () => {
        return (
            <div className="month_year_selectbox">
                <Form.Check
                    inline
                    label="E"
                    name={`env`}
                    type="radio"
                    id={`env`}
                    value={'ENVIRONMENTAL'}
                    checked={filter?.category === 'ENVIRONMENTAL' ? 'checked' : ''}
                    onChange={(e) => { setFilter({ ...filter, category: e.target.value }); }}
                />
                <Form.Check
                    inline
                    label="S"
                    name={`soc`}
                    type="radio"
                    id={`soc`}
                    value={'SOCIAL'}
                    checked={filter?.category === 'SOCIAL' ? 'checked' : ''}
                    onChange={(e) => { setFilter({ ...filter, category: e.target.value }); }}
                />
                <Form.Check
                    inline
                    label="G"
                    name={`gov`}
                    type="radio"
                    id={`gov`}
                    value={'GOVERNANCE'}
                    checked={filter?.category === 'GOVERNANCE' ? 'checked' : ''}
                    onChange={(e) => { setFilter({ ...filter, category: e.target.value }); }}
                />
            </div>
        );
    };

    const renderTrends = (data) => {
        let result = null;
        data?.percentFooter?.forEach((f) => {
            if (f.comparison) {
                const hasIncreased = f.currentValue > f.comparedValue;
                result = (
                    <div className="performance_count">
                        {
                            f.comparison && hasIncreased
                                ?
                                <label className={f.comparisonStyle === 'HIGHER THE BETTER' ? 'text-success' : 'text-danger'}>
                                    <BiSolidUpvote className="font-18" />
                                </label>
                                :
                                <label className={f.comparisonStyle === 'HIGHER THE BETTER' ? 'text-danger' : 'text-success'}>
                                    <BiSolidDownvote className="font-18" />
                                </label>
                        }
                        {` ${f.comparisonInPercentage ? f.value : f.comparedValue}`} {`${f.uom}`} {t("comparedto.label")} {parseInt(selectedYear) - 1}
                    </div>
                );
            }
        });
        return result ?? t("NoDataAvailable.label")
    }

    const getBranchSubmissionsSummary = () => {
        const { periodStart, periodEnd } = getSelectedPeriod();
        FormService.getBranchSubmissionsSummary(periodStart, periodEnd).then(res => {
            if (res.status !== 200) return;
            setBranchSubmissionsSummary(res.data);
        }).catch(error => {
            console.error('Error fetching branch submissions summary:', error);
        });
    };

    const getBranchSummaryTotals = () => {
        const rows = branchSubmissionsSummary?.sections?.SUMMARY || [];
        const totals = rows.reduce((acc, row) => {
            acc.total += row.total ?? 0;
            acc.COMPLETED += row.statusCount?.COMPLETED ?? 0;
            acc.PENDING_APPROVAL += row.statusCount?.PENDING_APPROVAL ?? 0;
            acc.CORRECTION_PENDING += row.statusCount?.CORRECTION_PENDING ?? 0;
            acc.NOT_STARTED += row.statusCount?.NOT_STARTED ?? 0;
            return acc;
        }, { total: 0, COMPLETED: 0, PENDING_APPROVAL: 0, CORRECTION_PENDING: 0, NOT_STARTED: 0 });
        totals.percentage = totals.total > 0 ? (totals.COMPLETED / totals.total) * 100 : 0;
        return totals;
    };

    const getSelectedPeriod = () => {
        const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
        const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
        const isFinancialYear = dataFreqType === 'FY';
        return isFinancialYear
            ? getFyPeriodFromCompanyInfo(selectedYear, companyInfo?.financialYearStart, companyInfo?.financialYearEnd)
            : { periodStart: `${selectedYear}-01-01`, periodEnd: `${selectedYear}-12-31` };
    };

    const openStatusDrilldown = (statusKey, statusLabel) => {
        setStatusDrilldown({ statusKey, statusLabel });
        setStatusDrilldownRows([]);
        const { periodStart, periodEnd } = getSelectedPeriod();
        FormService.getBranchSubmissionsByStatus(periodStart, periodEnd, statusKey).then(res => {
            if (res.status !== 200) return;
            setStatusDrilldownRows(res.data?.branchDetails || []);
        }).catch(error => {
            console.error('Error fetching branch submissions by status:', error);
        });
    };

    const openBranchSummaryModal = () => {
        setShowBranchSummaryModal(true);
        const { periodStart, periodEnd } = getSelectedPeriod();
        FormService.getBranchSubmissionsFrequency(periodStart, periodEnd).then(res => {
            if (res.status !== 200) return;
            setBranchSubmissionsFrequency(res.data);
        }).catch(error => {
            console.error('Error fetching branch submissions frequency:', error);
        });
    };

    

    const renderDataCollectionOverview = () => {
        const totals = getBranchSummaryTotals();
        return (
            <div className="data-collection-overview">
                <div
                    className="data-collection-overview-progress"
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={openBranchSummaryModal}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openBranchSummaryModal()}
                >
                    <CircularProgressbar
                        value={totals.percentage.toFixed(0)}
                        text={`${totals.percentage.toFixed(0)}%`}
                        styles={buildStyles({
                            pathColor: '#5abb6a',
                            textColor: '#5abb6a',
                            trailColor: '#dee8ed'
                        })}
                    />
                    <h6 className="text-center pt-2 mb-0">{t("Completed.label")}</h6>
                </div>
                <div className="data-collection-overview-count text-center">
                    <h4 className="mb-0">{totals.COMPLETED} / {totals.total}</h4>
                    <div className="text-secondary">{t("Completed.label")}</div>
                </div>
                <ul className="data-collection-overview-list list-unstyled mb-0">
                    <li
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                        onClick={() => openStatusDrilldown('PENDING_APPROVAL', t("PendingApproval.label"))}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openStatusDrilldown('PENDING_APPROVAL', t("PendingApproval.label"))}
                    >
                        <span className="text-secondary">{t("PendingApproval.label")}</span>
                        <span>{totals.PENDING_APPROVAL}</span>
                    </li>
                    <li
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                        onClick={() => openStatusDrilldown('CORRECTION_PENDING', t("CorrectionPending.label"))}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openStatusDrilldown('CORRECTION_PENDING', t("CorrectionPending.label"))}
                    >
                        <span className="text-secondary">{t("CorrectionPending.label")}</span>
                        <span>{totals.CORRECTION_PENDING}</span>
                    </li>
                    <li
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                        onClick={() => openStatusDrilldown(null, t("Notstarted.label"))}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openStatusDrilldown(null, t("Notstarted.label"))}
                    >
                        <span className="text-secondary">{t("Notstarted.label")}</span>
                        <span>{totals.NOT_STARTED}</span>
                    </li>
                </ul>
            </div>
        );
    };

    const hasPerformanceHighlightsData = () => {
        if (performanceHighlights?.length === 0) return false;

        let hasData = false;
        for (let index = 0; index < performanceHighlights.length; index++) {
            const d = performanceHighlights[index];
            if (d && d?.percentFooter?.filter(p => p.comparison === true)?.length > 0) {
                hasData = true;
                break;
            }
        }
        return hasData;  
    }

    return (
        <>
            <div className="HomeCard mb-3">
                <div className="HomeCardhead">
                    <div className="HomeCardhead_in">
                        <h5>
                            {t("ActiveDataCollection.label")}
                            {itemsTotalCount > 0 && (
                                <span className="ms-2">
                                    ({itemsTotalCount} {t("InProgress.label")})
                                </span>
                            )}
                        </h5>
                    </div>
                </div>
                <div className="HomeCardBody">
                    {activeAssessments.length === 0 ? (
                        <div className="Nodatafound_box">
                            <h6><FcFinePrint className="me-2" />{t("Nodatafound.label")}</h6>
                        </div>
                    ) : (
                        <div 
                            ref={scrollContainerRef}
                            className={`active-data-collection-container ${activeAssessments.length > 2 ? 'scrollable-container' : ''}`}
                        >
                            {activeAssessments.map((assessment, index) => {
                            const submissionStatusCount = assessment.submissionStatusCount || {};
                            const inProgressCount = submissionStatusCount.IN_PROGRESS ?? 0;
                            const completedCount = submissionStatusCount.COMPLETED ?? 0;
                            const notStartedCount = submissionStatusCount.DRAFT ?? 0;
                            const notSharedCount = submissionStatusCount.NOT_SHARED ?? 0;
                            const pendingApprovalCount = submissionStatusCount.PENDING_APPROVAL ?? 0;
                            const correctionPendingCount = submissionStatusCount.CORRECTION_PENDING ?? 0;
                            const inProgressDirectCount = submissionStatusCount.IN_PROGRESS ?? 0;

                            // Calculate total count
                            const totalCount = completedCount +
                                inProgressDirectCount +
                                notStartedCount +
                                correctionPendingCount +
                                pendingApprovalCount;

                            // Format period: "2025-11-01" -> "2025, November"
                            const formatPeriod = (periodDate) => {
                                if (!periodDate) return '';
                                return moment(periodDate).format("YYYY, MMMM");
                            };

                            // Format expiry date: "2025-11-20" -> "20 November, 2025"
                            const formatExpiryDate = (expiryDate) => {
                                if (!expiryDate) return '';
                                return moment(expiryDate).format("DD MMMM, YYYY");
                            };

                            const handleAssessmentClick = (e) => {
                                e?.preventDefault();
                                // Navigate directly to the submissions page with the responses tab selected
                                navigate(`/company_admin/assessments/${assessment.id}/submissions/true?tab=res`, {
                                    state: {
                                        assessmentName: `${assessment.assessmentForm?.name || assessment.name || ''} - ${formatPeriod(assessment.period)}`,
                                        activeTab: 'responses',
                                        forceFilterRefresh: true
                                    }
                                });
                            };

                            const handleStatusClick = (e, statusType) => {
                                e?.preventDefault();
                                e?.stopPropagation();
                                
                                // Map status types to filter keys
                                let childStatusFilters = {};
                                let selectedStatuses = [];
                                
                                switch (statusType) {
                                    case 'COMPLETED':
                                        childStatusFilters = { COMPLETED: true };
                                        selectedStatuses = ['COMPLETED'];
                                        break;
                                    case 'IN_PROGRESS':
                                        childStatusFilters = { IN_PROGRESS: true };
                                        selectedStatuses = ['IN_PROGRESS'];
                                        break;
                                    case 'DRAFT':
                                        childStatusFilters = { DRAFT: true };
                                        selectedStatuses = ['DRAFT'];
                                        break;
                                    case 'NOT_SHARED':
                                        childStatusFilters = { NOT_SHARED: true };
                                        selectedStatuses = ['NOT_SHARED'];
                                        break;
                                    case 'PENDING_APPROVAL':
                                        // Set all 3 levels for pending approval
                                        childStatusFilters = {
                                            PENDING_APPROVAL_LEVEL_1: true,
                                            PENDING_APPROVAL_LEVEL_2: true,
                                            PENDING_APPROVAL_LEVEL_3: true
                                        };
                                        selectedStatuses = ['PENDING_APPROVAL_LEVEL_1', 'PENDING_APPROVAL_LEVEL_2', 'PENDING_APPROVAL_LEVEL_3'];
                                        break;
                                    case 'CORRECTION_PENDING':
                                        childStatusFilters = { CORRECTION_PENDING: true };
                                        selectedStatuses = ['CORRECTION_PENDING'];
                                        break;
                                    default:
                                        childStatusFilters = {};
                                        selectedStatuses = [];
                                }
                                
                                // Convert selected statuses to comma-separated string for API
                                const childStatus = selectedStatuses.length > 0 ? selectedStatuses.join(",") : null;
                                
                                // Determine tab and filter key based on status type
                                const isPendingApproval = statusType === 'PENDING_APPROVAL';
                                const tabKey = isPendingApproval ? 'approvals' : 'responses';
                                const tabParam = isPendingApproval ? 'app' : 'res';
                                const activeTabName = isPendingApproval ? 'approvals' : 'responses';
                                
                                // Clear any previous filters and save new filter to localStorage
                                const filterData = {
                                    parentStatusFilters: {},
                                    childStatusFilters: childStatusFilters,
                                    parentStatus: null,
                                    childStatus: childStatus,
                                    submittedDate: null,
                                    _timestamp: Date.now() // Add timestamp to force refresh
                                };
                                
                                // Remove any previously saved filter and save new one
                                const savedData = localStorage.getItem(LS_DCE_SUBMISSION_FILTER_KEY);
                                const allFilters = savedData ? JSON.parse(savedData) : {};
                                // Clear previous filter and set new one
                                allFilters[tabKey] = filterData;
                                localStorage.setItem(LS_DCE_SUBMISSION_FILTER_KEY, JSON.stringify(allFilters));
                                
                                // Navigate to submissions page with appropriate tab
                                navigate(`/company_admin/assessments/${assessment.id}/submissions/true?tab=${tabParam}`, {
                                    state: {
                                        assessmentName: `${assessment.assessmentForm?.name || assessment.name || ''} - ${formatPeriod(assessment.period)}`,
                                        activeTab: activeTabName,
                                        forceFilterRefresh: true // Flag to force filter refresh
                                    }
                                });
                            };

                            return (
                                <div key={assessment.id || index} className="active-data-collection-item">
                                    <h6
                                        className="assessment-title assessment-title-link"
                                        onClick={handleAssessmentClick}
                                    >
                                        {assessment.assessmentForm?.name || assessment.name || ''}
                                    </h6>
                                    <div className="row m-0">

                                        <div className="col-lg-4 col-md-12 p-0 mt-1">
                                            <div className="assessment-details">
                                                <div className="detail-item">
                                                    <div className="detail-label">{t("Period.label")}</div>
                                                    <div className="detail-value">{assessment.period
                                                            ? moment(assessment.period).format("MMM, YYYY")
                                                            : ''}</div>
                                                </div>
                                                <div className="detail-item">
                                                    <div className="detail-label">{t("ExpiryDate.label")}</div>
                                                    {/* <div className="detail-value">{formatExpiryDate(assessment.lastSubmissionDate)}</div> */}
                                                    <div className="detail-value">
                                                        {assessment.lastSubmissionDate
                                                            ? moment(assessment.lastSubmissionDate).format("MMM DD, YYYY")
                                                            : ''}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-8 col-md-12 p-0 dataablock_event mt-1">
                                            {/* <h6>{t("Status.label")}</h6> */}
                                            <div className="row m-0 status-columns">
                                                <div className="col-lg-4 col-md-12 p-0">
                                                <ul>
                                                    <li>
                                                        <BsCircle className="text-secondary" />
                                                        <span><b>{t("Total.label")}</b></span>
                                                        <span><b>{totalCount}</b></span>
                                                    </li>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                {t("CompletedallApprovals.label")}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <li 
                                                            className={completedCount > 0 ? "status-link" : ""}
                                                            onClick={(e) => completedCount > 0 && handleStatusClick(e, 'COMPLETED')}
                                                            style={completedCount > 0 ? { cursor: 'pointer' } : {}}
                                                        >
                                                            <BsFillCircleFill className="text-Medium" />
                                                            <span>{t("Completed.label")}</span>
                                                            <span>{completedCount}</span>
                                                        </li>
                                                    </OverlayTrigger>

                                                    <li 
                                                        className={inProgressCount > 0 ? "status-link" : ""}
                                                        onClick={(e) => inProgressCount > 0 && handleStatusClick(e, 'IN_PROGRESS')}
                                                        style={inProgressCount > 0 ? { cursor: 'pointer' } : {}}
                                                    >
                                                        <BsFillCircleFill className="text-High" />
                                                        <span>{t("InProgress.label")}</span>
                                                        <span>{inProgressCount}</span>
                                                    </li>
                                                    <li 
                                                        className={notStartedCount > 0 ? "status-link" : ""}
                                                        onClick={(e) => notStartedCount > 0 && handleStatusClick(e, 'DRAFT')}
                                                        style={notStartedCount > 0 ? { cursor: 'pointer' } : {}}
                                                    >
                                                        <BsFillCircleFill className="text-Low" />
                                                        <span>{t("Notstarted.label")}</span>
                                                        <span>{notStartedCount}</span>
                                                    </li>

                                                </ul>
                                                </div>
                                                <div className="col-lg-6 col-md-12 p-0">
                                                <ul>
                                                    {/* <li 
                                                        className={notSharedCount > 0 ? "status-link" : ""}
                                                        onClick={(e) => notSharedCount > 0 && handleStatusClick(e, 'NOT_SHARED')}
                                                        style={notSharedCount > 0 ? { cursor: 'pointer' } : {}}
                                                    >
                                                        <BsFillCircleFill className="text-secondary" />
                                                        <span>{t("NotShared.label")}</span>
                                                        <span>{notSharedCount}</span>
                                                    </li> */}
                                                    {correctionPendingCount > 0 && (
                                                        <li 
                                                            className="status-link"
                                                            onClick={(e) => handleStatusClick(e, 'CORRECTION_PENDING')}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <BsFillCircleFill className="text-danger" />
                                                            <span>{t("CorrectionPending.label")}</span>
                                                            <span>{correctionPendingCount}</span>
                                                        </li>
                                                    )}
                                                    {pendingApprovalCount > 0 && (
                                                        <li 
                                                            className="status-link"
                                                            onClick={(e) => handleStatusClick(e, 'PENDING_APPROVAL')}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <BsFillCircleFill className="text-Accepted" />
                                                            <span>{t("PendingApproval.label")}</span>
                                                            <span>{pendingApprovalCount}</span>
                                                        </li>
                                                    )}
                                                </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        </div>
                    )}
                </div>
                {itemsTotalCount > 2 && activeAssessments.length < itemsTotalCount && (
                    <div className="p-2 text-center">
                        <button 
                            className="btn btn-link view-more-btn" 
                            onClick={handleViewMore}
                            disabled={isLoadingMore}
                        >
                            {isLoadingMore ? t("Loading.label") : t("ViewMore.label")}
                        </button>
                    </div>
                )}
                <div className="HomeCardFooter" hidden={!hasDataCollectionViewPermission()}>
                    {t("ForMoreDetails.label")} <a href="#" onClick={gotoDataCollection}>{t("GoToDataCollection.label")}</a>
                </div>
            </div>

            <BranchSubmissionsSummaryModal
                show={showBranchSummaryModal}
                onHide={() => setShowBranchSummaryModal(false)}
                summary={branchSubmissionsSummary}
                frequencySummary={branchSubmissionsFrequency}
            />

            <BranchStatusListModal
                show={!!statusDrilldown}
                onHide={() => setStatusDrilldown(null)}
                rows={statusDrilldownRows}
                statusLabel={statusDrilldown?.statusLabel}
            />

            {/* <div className="HomeCard mb-3">
                <div className="HomeCardhead">
                    <div className="HomeCardhead_in">
                        <h5>{t("PerformanceHighlights.label")}</h5>                        
                        {renderESGSwitch()}
                    </div>
                </div>
                <div className="HomeCardBody">
                    <div className="Performance_Highlights">
                        <Table >
                            <tbody>
                                {!hasPerformanceHighlightsData() && renderEmptyDataLabel()}
                                {
                                    performanceHighlights?.map((data) => {
                                        const renderRow = data?.percentFooter?.filter(p => p.comparison === true)?.length > 0;
                                        if (!renderRow) return <></>;

                                        return (
                                            <>
                                                <tr>
                                                    <td>{data?.title}</td>
                                                    <td>
                                                        {renderTrends(data)}
                                                    </td>
                                                </tr>
                                            </>
                                        );
                                    })
                                }
                            </tbody>
                        </Table>
                    </div>
                </div>
                <div className="HomeCardFooter" hidden={!hasDashboardViewPermission()}>
                    {t("ForMoreDetails.label")} <a href="#" onClick={gotoDashboard}>{t("GoToDashboard.label")}</a>
                </div>
            </div>             */}
        </>
    );
};

export default withTranslation()(DataBlock);
